import { useEffect, useMemo, useState } from "react";
import BillingMonthGenerator from "./BillingMonthGenerator";
import BillingMonthUserChecks from "./BillingMonthUserChecks";
import BillingPersonFacilityUsers from "./BillingPersonFacilityUsers";
import BillingPersonMonthlySimple from "./BillingPersonMonthlySimple";

const STATUS = {
  unpaid:{ label:"未請求", color:"#e67e22", bg:"#fef3e2" },
  billed:{ label:"請求済み", color:"#457B9D", bg:"#e8f4fb" },
  paid:{ label:"入金済み", color:"#27ae60", bg:"#eafaf1" },
  overdue:{ label:"未収", color:"#e74c3c", bg:"#fdf0ef" },
};

const currentYm = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone:"Asia/Tokyo", year:"numeric", month:"2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(part=>[part.type, part.value]));
  return `${values.year}-${values.month}`;
};
const inputStyle = {
  width:"100%", boxSizing:"border-box", border:"1px solid #d1d5db", borderRadius:7,
  padding:"9px 10px", fontSize:13, background:"#fff",
};
const responsiveBillingNavigationStyles = `
  @media (max-width: 600px) {
    .jippi-billing-section-nav {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    .jippi-person-billing-filters {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }
  .jippi-person-month-input::-webkit-date-and-time-value,
  .jippi-person-month-input::-webkit-datetime-edit {
    min-width: 0;
    padding: 0;
    text-align: left;
  }
`;

const recordName = record => record.service_user_name || record.user_name || "利用者不明";
const recordYm = record => record.ym || record.year_month || record.yearMonth || "";
const reviewMoney = value => `${Number(value || 0).toLocaleString("ja-JP")}円`;
const reviewSignedMoney = value => `${Number(value || 0) > 0 ? "+" : ""}${Number(value || 0).toLocaleString("ja-JP")}円`;
const reviewDateTime = value => String(value || "—").replace(/^([0-9]{4})-([0-9]{2})-([0-9]{2})[ T]([0-9]{2}):([0-9]{2}).*/,"$1/$2/$3 $4:$5");
const requestId = () => window.crypto?.randomUUID?.()
  || "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, character => {
    const value = Math.floor(Math.random() * 16);
    return (character === "x" ? value : (value & 3) | 8).toString(16);
  });
const NEXT_STATUS = {
  unpaid:["billed"], billed:["paid","overdue"], overdue:["paid"], paid:[],
};
const PAYMENT_METHOD = { bank_transfer:"銀行振込", cash:"現金手渡し" };

export function JippiEntryForm({ store, request, initial = null, onCancel, onSaved }) {
  const [form, setForm] = useState(() => ({
    yearMonth:recordYm(initial || {}) || currentYm(),
    serviceUserId:initial?.service_user_id || "",
    itemCode:initial?.item_code || "",
    amount:initial?.amount ?? "",
    billedDate:initial?.billed_date || "",
    note:initial?.note || "",
  }));
  const [serviceUsers, setServiceUsers] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoadingOptions(true);
    request(`/jippi.php?action=entry-options&storeId=${encodeURIComponent(store.id)}&ym=${encodeURIComponent(form.yearMonth)}`)
      .then(data=>{
        if (!active) return;
        setServiceUsers(data.service_users || []);
        setError("");
      })
      .catch(requestError=>active && setError(requestError.message))
      .finally(()=>active && setLoadingOptions(false));
    return () => { active = false; };
  }, [store.id, form.yearMonth, request]);

  const selectedUser = serviceUsers.find(user=>user.id === form.serviceUserId);
  const rules = selectedUser?.billing_rules || [];

  const submit = async event => {
    event.preventDefault();
    setError("");
    if (!form.serviceUserId || !form.itemCode) {
      setError("利用者と請求項目を選択してください");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id:initial?.id,
        storeId:store.id,
        yearMonth:form.yearMonth,
        serviceUserId:form.serviceUserId,
        itemCode:form.itemCode,
        amount:Number(form.amount),
        billedDate:form.billedDate,
        note:form.note,
        requestId:requestId(),
        expectedRevision:initial?.revision,
        reason:initial ? "月次合計金額の修正" : "月次合計金額の登録",
        ...(initial ? {} : { status:"unpaid" }),
      };
      const data = await request("/jippi.php", initial ? "PUT" : "POST", payload);
      onSaved(data.record);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return <form onSubmit={submit} style={{ background:"#f8fafc", border:`2px solid ${store.accent}40`, borderRadius:12, padding:18, marginBottom:16 }}>
    <div style={{ fontWeight:800, color:store.color, marginBottom:12 }}>{initial ? "実費記録を編集・連携" : "実費請求 新規登録"}</div>
    {initial && !initial.service_user_id && <div style={{ background:"#fff7ed", color:"#9a3412", borderRadius:7, padding:"8px 10px", marginBottom:10, fontSize:12 }}>この既存データは未連携です。内容を確認して利用者と請求項目を手動で選択してください。</div>}
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))", gap:10 }}>
      <label style={{ fontSize:11, color:"#555" }}>対象年月
        <input aria-label="実費対象年月" type="month" value={form.yearMonth} onChange={event=>setForm(current=>({ ...current, yearMonth:event.target.value, serviceUserId:"", itemCode:"" }))} required style={inputStyle}/>
      </label>
      <label style={{ fontSize:11, color:"#555" }}>利用者
        <select aria-label="実費利用者" value={form.serviceUserId} onChange={event=>setForm(current=>({ ...current, serviceUserId:event.target.value, itemCode:"" }))} disabled={loadingOptions} required style={inputStyle}>
          <option value="">利用者を選択</option>
          {serviceUsers.map(user=><option key={user.id} value={user.id}>{user.name}{user.billing_rules.length === 0 ? "（請求ルールなし）" : ""}</option>)}
        </select>
      </label>
      <label style={{ fontSize:11, color:"#555" }}>請求項目
        <select aria-label="実費請求項目" value={form.itemCode} onChange={event=>setForm(current=>({ ...current, itemCode:event.target.value }))} disabled={!selectedUser || rules.length === 0} required style={inputStyle}>
          <option value="">請求項目を選択</option>
          {rules.map(rule=><option key={rule.id} value={rule.item_code}>{rule.item_label}（{rule.item_code}）</option>)}
        </select>
      </label>
      <label style={{ fontSize:11, color:"#555" }}>金額（円）
        <input aria-label="実費金額" type="number" min="1" step="1" value={form.amount} onChange={event=>setForm(current=>({ ...current, amount:event.target.value }))} required style={inputStyle}/>
      </label>
      <label style={{ fontSize:11, color:"#555" }}>請求日（任意）
        <input aria-label="実費請求日" type="date" value={form.billedDate} onChange={event=>setForm(current=>({ ...current, billedDate:event.target.value }))} style={inputStyle}/>
      </label>
      <label style={{ fontSize:11, color:"#555" }}>備考（任意）
        <input aria-label="実費備考" value={form.note} onChange={event=>setForm(current=>({ ...current, note:event.target.value }))} style={inputStyle}/>
      </label>
    </div>
    {selectedUser && rules.length === 0 && <div style={{ color:"#b45309", fontSize:12, marginTop:8 }}>対象月に有効な請求ルールがありません。先に利用者詳細で請求ルールを登録してください。</div>}
    {error && <div role="alert" style={{ color:"#b91c1c", fontSize:12, marginTop:8 }}>{error}</div>}
    <div style={{ display:"flex", gap:8, marginTop:12 }}>
      <button type="submit" disabled={saving || loadingOptions} style={{ background:store.color, color:"#fff", border:0, borderRadius:7, padding:"9px 16px", fontWeight:700 }}>{saving ? "保存中…" : "保存"}</button>
      <button type="button" onClick={onCancel}>キャンセル</button>
    </div>
  </form>;
}

function JippiReviewDetail({ review, store, request, canConfirm, onBack, onReconfirmed }) {
  const [showUnchanged,setShowUnchanged] = useState(false);
  const [showConfirmation,setShowConfirmation] = useState(false);
  const [saving,setSaving] = useState(false);
  const [error,setError] = useState("");
  const reconfirm = async () => {
    setSaving(true);
    setError("");
    try {
      await request("/billing_month_user_checks.php", "PUT", {
        id:review.check_id,
        expectedRevision:review.revision,
        status:Number(review.current_amount) > 0 ? "amount_confirmed" : "no_charge",
      });
      setShowConfirmation(false);
      onReconfirmed();
    } catch (requestError) {
      setShowConfirmation(false);
      setError(requestError.status === 409
        ? "内容が更新されました。実費請求チェックから最新の変更内容を開き直してください。"
        : requestError.message || "再確定に失敗しました");
    } finally {
      setSaving(false);
    }
  };
  const rows = showUnchanged ? [...review.changes,...review.unchanged] : review.changes;
  return <div aria-label={`${review.service_user_name} 実費差分詳細`}>
    <button type="button" onClick={onBack} style={{ marginBottom:10, background:"none", border:"none", color:store.accent, fontWeight:800 }}>← 実費明細へ戻る</button>
    <section style={{ background:"#fff", border:"2px solid #fecaca", borderRadius:12, overflow:"hidden" }}>
      <div style={{ padding:"14px 16px", background:"#7f1d1d", color:"#fff" }}>
        <div style={{ fontSize:11, fontWeight:800 }}>再確認モード／差分表示</div>
        <div style={{ marginTop:3, fontSize:20, fontWeight:900 }}>{review.service_user_name}</div>
        <div style={{ marginTop:2, fontSize:12 }}>{review.ym} の実費明細</div>
      </div>
      <div style={{ padding:14 }}>
        <div aria-label="再確認差分集計" style={{ display:"grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", gap:7 }}>
          {[
            ["前回確定額",reviewMoney(review.previous_amount)],
            ["現在金額",reviewMoney(review.current_amount)],
            ["差額",reviewSignedMoney(review.difference)],
            ["変更件数",`${review.change_count}件`],
          ].map(([label,value])=><div key={label} style={{ minWidth:0, padding:"9px 8px", background:"#fff7f7", border:"1px solid #fecaca", borderRadius:7 }}><div style={{ color:"#7f1d1d", fontSize:10 }}>{label}</div><div style={{ marginTop:2, color:"#991b1b", fontSize:17, fontWeight:900, overflowWrap:"anywhere" }}>{value}</div></div>)}
        </div>
        <div style={{ marginTop:10, padding:"9px 10px", borderRadius:7, background:"#f8fafc", color:"#475569", fontSize:11, lineHeight:1.55 }}>
          <b>変更日時：</b>{reviewDateTime(review.changed_at)}　<b>変更者：</b>{review.changed_by_name || "—"}<br/>
          前回確定：{reviewDateTime(review.confirmed_at)}・{review.confirmed_by_name || "—"}
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, flexWrap:"wrap", marginTop:14 }}>
          <div style={{ fontWeight:900, color:"#7f1d1d" }}>変更された明細を優先表示</div>
          {review.unchanged.length > 0 && <button type="button" onClick={()=>setShowUnchanged(current=>!current)}>{showUnchanged ? "変更なしを非表示" : `変更なしの項目も表示（${review.unchanged.length}件）`}</button>}
        </div>
        <div style={{ display:"grid", gap:8, marginTop:9 }}>
          {rows.map(change=>{
            const changed = change.types.length > 0;
            const amountDifference = Number(change.current?.amount || 0) - Number(change.previous?.amount || 0);
            return <article key={change.key} aria-label={`${change.types.join("・") || "変更なし"} ${change.current?.item || change.previous?.item || "状態変更"}`} style={{ padding:"10px 11px", border:`1px solid ${changed ? "#fca5a5" : "#e2e8f0"}`, borderLeft:`5px solid ${changed ? "#dc2626" : "#94a3b8"}`, borderRadius:8, background:changed ? "#fff7f7" : "#fff" }}>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                {(change.types.length ? change.types : ["変更なし"]).map(type=><span key={type} style={{ padding:"2px 7px", borderRadius:20, background:changed ? "#fee2e2" : "#f1f5f9", color:changed ? "#991b1b" : "#475569", fontSize:10, fontWeight:900 }}>{type}</span>)}
              </div>
              <div style={{ marginTop:6, fontSize:14, fontWeight:900 }}>{change.current?.item || change.previous?.item || change.description}</div>
              {change.previous && change.current && <div style={{ marginTop:5, fontSize:12, lineHeight:1.6 }}>
                {change.field_changes?.map(field=><div key={field.field}><b>{field.field}変更あり：</b>{field.before} → {field.after}</div>)}
                {amountDifference !== 0 && <div><b>差額：</b>{reviewSignedMoney(amountDifference)}</div>}
              </div>}
              {!change.previous && change.current && <div style={{ marginTop:5, fontSize:12 }}><b>追加：</b>{change.current.item}・{reviewMoney(change.current.amount)}</div>}
              {change.previous && !change.current && <div style={{ marginTop:5, fontSize:12 }}><b>削除された旧明細：</b>{change.previous.item}・{reviewMoney(change.previous.amount)}</div>}
              <div style={{ marginTop:6, color:"#64748b", fontSize:10, lineHeight:1.45 }}>{change.description || "変更なし"}{changed && <>　／　{reviewDateTime(change.changed_at)}・{change.changed_by_name}</>}</div>
            </article>;
          })}
        </div>
        {error && <div role="alert" style={{ marginTop:10, color:"#991b1b", fontSize:12, fontWeight:800 }}>{error}</div>}
        {canConfirm ? <button type="button" disabled={saving} onClick={()=>setShowConfirmation(true)} style={{ width:"100%", minHeight:50, marginTop:14, border:0, borderRadius:8, background:store.color, color:"#fff", fontWeight:900 }}>
          {saving ? "保存中…" : `変更内容を確認して${reviewMoney(review.current_amount)}で再確定`}
        </button> : <div style={{ marginTop:12, color:"#64748b", fontSize:12 }}>閲覧専用のため再確定できません。</div>}
      </div>
    </section>
    {showConfirmation && <div role="presentation" style={{ position:"fixed", inset:0, zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16, background:"rgba(15,23,42,.58)" }}>
      <div role="dialog" aria-modal="true" aria-labelledby="jippi-reconfirmation-title" style={{ width:"100%", maxWidth:420, boxSizing:"border-box", background:"#fff", borderRadius:12, padding:18 }}>
        <div id="jippi-reconfirmation-title" style={{ fontSize:18, fontWeight:900 }}>変更後の請求金額を再確定しますか？</div>
        <div style={{ marginTop:7, color:"#475569", fontSize:12 }}>差分確認後の最終確認です。</div>
        <dl style={{ display:"grid", gap:8, margin:"14px 0 0" }}>
          {[["利用者",review.service_user_name],["対象月",review.ym],["確定金額",reviewMoney(review.current_amount)]].map(([label,value])=><div key={label} style={{ display:"grid", gridTemplateColumns:"72px minmax(0,1fr)", gap:8, padding:"9px 10px", background:"#f8fafc", borderRadius:7 }}><dt style={{ color:"#64748b", fontSize:11 }}>{label}</dt><dd style={{ margin:0, fontWeight:900 }}>{value}</dd></div>)}
        </dl>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:9, marginTop:16 }}>
          <button type="button" disabled={saving} onClick={()=>setShowConfirmation(false)} style={{ minHeight:48 }}>戻る／キャンセル</button>
          <button type="button" disabled={saving} onClick={reconfirm} style={{ minHeight:48, border:0, borderRadius:8, background:store.color, color:"#fff", fontWeight:900 }}>再確定する</button>
        </div>
      </div>
    </div>}
  </div>;
}

export default function JippiTab({ store, request, access }) {
  const [section, setSection] = useState("checks");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewChanges, setReviewChanges] = useState([]);
  const [checksYearMonth, setChecksYearMonth] = useState("");
  const [personYearMonth, setPersonYearMonth] = useState(currentYm);
  const [personBillingDirty, setPersonBillingDirty] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterYM, setFilterYM] = useState("all");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const facilities = useMemo(
    ()=>Array.isArray(access?.facilities) ? access.facilities : [],
    [access],
  );
  const [detailStoreId, setDetailStoreId] = useState("");

  useEffect(() => {
    const initial = facilities.find(facility=>facility.id === store.id) || facilities[0];
    setDetailStoreId(current=>facilities.some(facility=>facility.id === current) ? current : (initial?.id || ""));
  }, [facilities, store.id]);

  const selectedFacility = facilities.find(facility=>facility.id === detailStoreId) || null;
  const sectionStore = selectedFacility ? {
    ...selectedFacility,
    color:selectedFacility.id === store.id ? store.color : "#334155",
    accent:selectedFacility.id === store.id ? store.accent : "#64748b",
  } : null;
  const capabilities = selectedFacility?.capabilities || {};
  const sectionStoreId = sectionStore?.id || "";

  useEffect(() => {
    if (section !== "details" || !sectionStoreId) return undefined;
    let active = true;
    setLoading(true);
    request(`/jippi.php?storeId=${encodeURIComponent(sectionStoreId)}`)
      .then(data=>{ if (active) { setRecords(data.records || []); setReviewChanges(data.review_changes || []); setError(""); } })
      .catch(requestError=>active && setError(requestError.message))
      .finally(()=>active && setLoading(false));
    return () => { active = false; };
  }, [section, sectionStoreId, request]);

  const applySaved = saved => {
    setRecords(current=>current.some(record=>Number(record.id) === Number(saved.id))
      ? current.map(record=>Number(record.id) === Number(saved.id) ? saved : record)
      : [saved, ...current]);
    setShowForm(false);
    setEditing(null);
    setViewId(saved.id);
  };

  const updateRecord = async (record, changes) => {
    const statusOnly = Object.keys(changes).every(key=>["status","paymentMethod"].includes(key));
    if ((statusOnly && !capabilities.can_confirm) || (!statusOnly && !capabilities.can_edit)) return false;
    try {
      const data = await request("/jippi.php", "PUT", {
        id:record.id,
        expectedRevision:record.revision,
        requestId:requestId(),
        reason:Object.prototype.hasOwnProperty.call(changes, "status") ? "請求・入金状態の更新" : "月次合計金額の修正",
        ...changes,
      });
      applySaved(data.record);
      return true;
    } catch (requestError) {
      setError(requestError.message);
      return false;
    }
  };

  const allYMs = [...new Set(records.map(recordYm).filter(Boolean))].sort().reverse();
  const filtered = useMemo(() => records.filter(record =>
    (filterYM === "all" || recordYm(record) === filterYM)
    && (filterStatus === "all" || record.status === filterStatus)
  ), [records, filterYM, filterStatus]);
  const total = filtered.reduce((sum, record)=>sum + Number(record.amount || 0), 0);
  const byUser = {};
  filtered.forEach(record=>{ const name=recordName(record); byUser[name]=(byUser[name] || 0) + Number(record.amount || 0); });

  const selected = records.find(record=>Number(record.id) === Number(viewId));
  const changeSection = next => {
    setSection(next);
    setViewId(null);
    setReviewTarget(null);
    setEditing(null);
    setShowForm(false);
    setError("");
  };
  const openReviewChange = review => {
    setDetailStoreId(review.facility_id);
    setSection("details");
    setViewId(null);
    setEditing(null);
    setShowForm(false);
    setError("");
    setReviewTarget(review);
  };
  const confirmPersonBillingMove = () => !personBillingDirty || window.confirm(
    "未保存の入力があります。\n「OK」で変更を破棄して移動し、「キャンセル」で編集を続けます。",
  );
  const navigation = <><style>{responsiveBillingNavigationStyles}</style><nav className="jippi-billing-section-nav" aria-label="実費請求内メニュー" style={{ display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:6, width:"100%", maxWidth:"100%", minWidth:0, boxSizing:"border-box", padding:"2px 1px 8px", marginBottom:12 }}>
    {[
      ["checks","実費請求チェック"],
      ["person","人物月次請求"],
      ["monthly","月次漏れ確認"],
      ["details","実費明細"],
    ].map(([key,label])=><button className="jippi-billing-section-tab" key={key} type="button" aria-current={section === key ? "page" : undefined} onClick={()=>changeSection(key)} style={{ width:"100%", minWidth:0, minHeight:48, padding:"8px 4px", border:`1px solid ${section === key ? store.accent : "#cbd5e1"}`, borderRadius:9, background:section === key ? store.color : "#fff", color:section === key ? "#fff" : "#475569", fontSize:16, lineHeight:1.25, fontWeight:900, whiteSpace:"nowrap" }}>{label}</button>)}
  </nav></>;
  const sectionDescriptions = {
    checks:"利用者ごとに、その月の実費合計と確認状態を確認し、請求する金額を確定します。",
    person:"人物ごとに月1件の合計金額を入力し、社長承認、請求、入金を管理します。",
    monthly:"対象利用者について実費の確認漏れがないか確認し、月締め前の未処理を確認します。",
    details:"その月に登録された実費明細を確認します。漏れや誤りがある場合は確認・修正します。",
  };
  const sectionDescription = <div aria-label="選択中タブの説明" style={{ margin:"-4px 1px 12px", padding:"8px 10px", borderRadius:7, background:"#f8fafc", color:"#475569", fontSize:12, lineHeight:1.55 }}>
    {sectionDescriptions[section]}
  </div>;
  const facilityControlLabel = section === "person" ? "人物月次請求" : section === "monthly" ? "月次漏れ確認" : "実費明細";
  const facilitySelectStyle = section === "person"
    ? { display:"block", width:"100%", maxWidth:"100%", minWidth:0, minHeight:52, boxSizing:"border-box", marginTop:5, padding:"10px 12px", border:"1px solid #cbd5e1", borderRadius:7, background:"#fff", fontSize:20, lineHeight:1.25, fontWeight:800 }
    : { display:"block", width:"100%", minWidth:0, minHeight:44, marginTop:4, padding:"8px 10px", border:"1px solid #cbd5e1", borderRadius:7, background:"#fff", fontWeight:800 };
  const facilitySelect = <select aria-label={`${facilityControlLabel}施設`} value={detailStoreId} onChange={event=>{
    if (section === "person" && !confirmPersonBillingMove()) { event.target.value = detailStoreId; return; }
    setPersonBillingDirty(false);
    setDetailStoreId(event.target.value); setViewId(null); setReviewTarget(null); setEditing(null); setShowForm(false);
  }} style={facilitySelectStyle}>
      {facilities.map(facility=><option key={facility.id} value={facility.id}>{facility.name}</option>)}
    </select>;
  const facilityControl = section !== "checks" && (section === "person" ? <div className="jippi-person-billing-filters" aria-label="人物月次請求条件" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,220px),1fr))", gap:10, width:"100%", maxWidth:"100%", minWidth:0, boxSizing:"border-box", marginBottom:12 }}>
    <label style={{ display:"block", width:"100%", maxWidth:"100%", minWidth:0, fontSize:12, color:"#475569" }}>施設{facilitySelect}</label>
    <label style={{ display:"block", width:"100%", maxWidth:"100%", minWidth:0, fontSize:12, color:"#475569" }}>対象月
      <span aria-label="人物月次対象月枠" style={{ display:"flex", alignItems:"center", width:"100%", maxWidth:"100%", minWidth:0, minHeight:52, boxSizing:"border-box", marginTop:5, padding:"10px 12px", border:"1px solid #cbd5e1", borderRadius:7, background:"#fff" }}>
        <input className="jippi-person-month-input" aria-label="人物月次対象月" type="month" value={personYearMonth} onChange={event=>{
          if (!confirmPersonBillingMove()) { event.target.value = personYearMonth; return; }
          setPersonBillingDirty(false);
          setPersonYearMonth(event.target.value);
        }} style={{ display:"block", width:"100%", maxWidth:"100%", minWidth:0, boxSizing:"border-box", margin:0, padding:0, border:0, borderRadius:0, background:"transparent", fontFamily:"inherit", fontSize:20, lineHeight:1.25, fontWeight:800 }}/>
      </span>
    </label>
  </div> : <label style={{ display:"block", fontSize:11, color:"#475569", marginBottom:12 }}>施設{facilitySelect}</label>);

  if (section === "checks") return <div>{navigation}{sectionDescription}<BillingMonthUserChecks store={store} request={request} access={access} onReviewChanges={openReviewChange} initialYearMonth={checksYearMonth}/></div>;
  if (!sectionStore) return <div>{navigation}{sectionDescription}<div role="alert">実費請求を閲覧できる施設はありません。</div></div>;
  if (section === "person") return <div>{navigation}{sectionDescription}{facilityControl}<BillingPersonFacilityUsers storeId={sectionStoreId} request={request} yearMonth={personYearMonth} onYearMonthChange={setPersonYearMonth} showList={false}/><BillingPersonMonthlySimple request={request} storeId={sectionStoreId} yearMonth={personYearMonth} onYearMonthChange={setPersonYearMonth} onDirtyChange={setPersonBillingDirty}/></div>;
  if (section === "monthly") return <div>{navigation}{sectionDescription}{facilityControl}<BillingMonthGenerator store={sectionStore} request={request}/></div>;

  if (reviewTarget) return <div>
    {navigation}
    {sectionDescription}
    {facilityControl}
    <JippiReviewDetail
      review={reviewTarget}
      store={sectionStore}
      request={request}
      canConfirm={Boolean(capabilities.can_confirm)}
      onBack={()=>setReviewTarget(null)}
      onReconfirmed={()=>{ setChecksYearMonth(reviewTarget.ym); setReviewTarget(null); setSection("checks"); }}
    />
  </div>;

  if (selected) {
    const status = STATUS[selected.status] || STATUS.unpaid;
    const billingProhibited = Boolean(selected.billing_policy?.billing_prohibited);
    return <div>
      {navigation}
      {sectionDescription}
      {facilityControl}
      <button type="button" onClick={()=>{ setViewId(null); setEditing(null); }} style={{ marginBottom:14, background:"none", border:"none", color:sectionStore.accent, fontWeight:700 }}>← 戻る</button>
      {editing && capabilities.can_edit && <JippiEntryForm store={sectionStore} request={request} initial={editing} onCancel={()=>setEditing(null)} onSaved={applySaved}/>}
      <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, overflow:"hidden" }}>
        <div style={{ background:`linear-gradient(135deg,${sectionStore.color},${sectionStore.accent})`, color:"#fff", padding:"20px 24px" }}>
          <div style={{ fontSize:12, opacity:.8 }}>実費請求</div>
          <div style={{ fontSize:20, fontWeight:800 }}>{recordName(selected)}</div>
          <div style={{ fontSize:30, fontWeight:900 }}>¥{Number(selected.amount || 0).toLocaleString()}</div>
        </div>
        <div style={{ padding:20 }}>
          {billingProhibited && <div role="status" style={{ marginBottom:14, padding:"10px 12px", border:"1px solid #f59e0b", borderRadius:8, background:"#fffbeb", color:"#92400e", fontWeight:800 }}>2026年9月は練習月・請求禁止です。入力内容は編集できますが、請求・未収・入金状態へ変更できません。</div>}
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:14 }}>
            <span style={{ background:status.bg, color:status.color, borderRadius:20, padding:"4px 12px", fontSize:12, fontWeight:800 }}>{status.label}</span>
            <span style={{ background:selected.service_user_id && selected.item_code ? "#dcfce7" : "#fff7ed", color:selected.service_user_id && selected.item_code ? "#166534" : "#9a3412", borderRadius:20, padding:"4px 12px", fontSize:12, fontWeight:800 }}>
              {selected.service_user_id && selected.item_code ? "利用者・請求項目連携済み" : "未連携の既存データ"}
            </span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))", gap:10 }}>
            {[
              ["対象年月", recordYm(selected)], ["記録時利用者名", selected.user_name],
              ["利用者ID", selected.service_user_id || "—"], ["請求項目", selected.item],
              ["項目コード", selected.item_code || "—"], ["請求日", selected.billed_date || "未設定"],
              ["集計担当者・日時", selected.collected_by_name ? `${selected.collected_by_name}・${reviewDateTime(selected.collected_at)}` : "—"],
              ["最終変更者・日時", selected.last_changed_by_name ? `${selected.last_changed_by_name}・${reviewDateTime(selected.last_changed_at)}` : "—"],
              ["入金方法", selected.payment_method ? PAYMENT_METHOD[selected.payment_method] || selected.payment_method : "未設定"],
              ["revision", selected.revision],
              ["備考", selected.note || "なし"],
            ].map(([label,value])=><div key={label} style={{ background:"#f8fafc", borderRadius:8, padding:"9px 11px" }}><div style={{ fontSize:10, color:"#64748b" }}>{label}</div><div style={{ fontSize:13, fontWeight:600, overflowWrap:"anywhere" }}>{value}</div></div>)}
          </div>
          {capabilities.can_edit && selected.status === "unpaid" && <button type="button" onClick={()=>setEditing(selected)} style={{ marginTop:14 }}>実費内容・連携を編集</button>}
          {capabilities.can_edit && selected.status === "unpaid" && <div style={{ display:"flex", gap:8, marginTop:14 }}>
            <input aria-label="実費備考更新" value={note} onChange={event=>setNote(event.target.value)} placeholder="備考を更新" style={{ ...inputStyle, flex:1 }}/>
            <button type="button" onClick={async()=>{ if (await updateRecord(selected, { note })) setNote(""); }}>備考更新</button>
          </div>}
          {!billingProhibited && capabilities.can_confirm && (NEXT_STATUS[selected.status] || []).includes("paid") && <label style={{ display:"block", marginTop:14, fontSize:11, color:"#475569" }}>入金方法
            <select aria-label="実費入金方法" value={paymentMethod} onChange={event=>setPaymentMethod(event.target.value)} style={{ ...inputStyle, marginTop:4 }}>
              {Object.entries(PAYMENT_METHOD).map(([key,label])=><option key={key} value={key}>{label}</option>)}
            </select>
          </label>}
          {!billingProhibited && capabilities.can_confirm && <div style={{ display:"flex", gap:7, flexWrap:"wrap", marginTop:14 }}>
            {(NEXT_STATUS[selected.status] || []).map(key=><button key={key} type="button" onClick={()=>updateRecord(selected, {
              status:key,
              ...(key === "paid" ? { paymentMethod } : {}),
            })}>{STATUS[key].label}に変更</button>)}
          </div>}
          {selected.status === "paid" && <div style={{ marginTop:12, color:"#166534", fontSize:12, fontWeight:800 }}>入金済みのため通常編集はロックされています。</div>}
          {!capabilities.can_edit && !capabilities.can_confirm && <div style={{ marginTop:14, color:"#64748b", fontSize:12 }}>閲覧専用です。実費明細の編集・状態変更はできません。</div>}
          {error && <div role="alert" style={{ color:"#b91c1c", marginTop:10 }}>{error}</div>}
        </div>
      </div>
    </div>;
  }

  return <div>
    {navigation}
    {sectionDescription}
    {facilityControl}
    {reviewChanges.length > 0 && <section aria-label="再確認が必要な利用者" style={{ marginBottom:14, padding:10, border:"1px solid #fecaca", borderRadius:9, background:"#fff7f7" }}>
      <div style={{ color:"#991b1b", fontSize:12, fontWeight:900 }}>再確認が必要な利用者</div>
      <div style={{ display:"grid", gap:6, marginTop:7 }}>
        {reviewChanges.map(review=><button key={`${review.ym}-${review.service_user_id}`} type="button" onClick={()=>setReviewTarget(review)} style={{ minHeight:44, padding:"8px 10px", textAlign:"left", border:"1px solid #fca5a5", borderRadius:7, background:"#fff", color:"#7f1d1d" }}>
          <b>{review.service_user_name}</b>　{review.ym}　{reviewMoney(review.previous_amount)} → {reviewMoney(review.current_amount)}（{reviewSignedMoney(review.difference)}）
        </button>)}
      </div>
    </section>}
    <div style={{ display:"flex", justifyContent:"space-between", gap:8, flexWrap:"wrap", marginBottom:14 }}>
      <div style={{ display:"flex", gap:8 }}>
        <select aria-label="実費年月フィルター" value={filterYM} onChange={event=>setFilterYM(event.target.value)}><option value="all">全期間</option>{allYMs.map(ym=><option key={ym} value={ym}>{ym}</option>)}</select>
        <select aria-label="実費状態フィルター" value={filterStatus} onChange={event=>setFilterStatus(event.target.value)}><option value="all">すべて</option>{Object.entries(STATUS).map(([key,value])=><option key={key} value={key}>{value.label}</option>)}</select>
      </div>
      {capabilities.can_create && <button type="button" onClick={()=>setShowForm(current=>!current)} style={{ background:sectionStore.accent, color:"#fff", border:0, borderRadius:7, padding:"9px 15px", fontWeight:700 }}>{showForm ? "キャンセル" : "+ 新規登録"}</button>}
    </div>
    {showForm && capabilities.can_create && <JippiEntryForm store={sectionStore} request={request} onCancel={()=>setShowForm(false)} onSaved={applySaved}/>}
    {!capabilities.can_create && !capabilities.can_edit && !capabilities.can_confirm && <div style={{ marginBottom:10, color:"#64748b", fontSize:12 }}>閲覧専用です。実費明細の登録・編集・状態変更はできません。</div>}
    {error && <div role="alert" style={{ color:"#b91c1c", marginBottom:10 }}>{error}</div>}
    {loading ? <div>読み込み中…</div> : <>
      <div style={{ background:sectionStore.color+"10", borderRadius:9, padding:"10px 14px", marginBottom:12 }}><b>合計 ¥{total.toLocaleString()}</b></div>
      {filtered.length > 0 && <div style={{ display:"flex", gap:7, flexWrap:"wrap", marginBottom:12 }}>{Object.entries(byUser).map(([name,amount])=><span key={name} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:7, padding:"5px 9px", fontSize:12 }}>{name} <b>¥{amount.toLocaleString()}</b></span>)}</div>}
      <div style={{ display:"grid", gap:8 }}>
        {filtered.map(record=>{ const status=STATUS[record.status] || STATUS.unpaid; const linked=record.service_user_id && record.item_code; return <button type="button" key={record.id} onClick={()=>setViewId(record.id)} style={{ textAlign:"left", background:"#fff", border:"1px solid #e5e7eb", borderLeft:`4px solid ${status.color}`, borderRadius:9, padding:"11px 13px", cursor:"pointer" }}>
          <div style={{ display:"flex", justifyContent:"space-between", gap:10 }}><div><b>{recordName(record)}</b><div style={{ fontSize:11, color:"#64748b", marginTop:3 }}>{recordYm(record)} ・ {record.item} {record.item_code ? `（${record.item_code}）` : ""}</div><div style={{ fontSize:10, color:linked ? "#15803d" : "#b45309", marginTop:3 }}>{linked ? "ID連携済み" : "未連携の既存データ"}</div>{record.billing_policy?.billing_prohibited && <div style={{ fontSize:10, color:"#92400e", fontWeight:800, marginTop:3 }}>練習月・請求禁止</div>}</div><div style={{ textAlign:"right" }}><b>¥{Number(record.amount || 0).toLocaleString()}</b><div style={{ color:status.color, fontSize:11, fontWeight:700 }}>{status.label}</div></div></div>
        </button>; })}
        {filtered.length === 0 && <div style={{ color:"#94a3b8", textAlign:"center", padding:28 }}>データなし</div>}
      </div>
    </>}
  </div>;
}
